/**
 * Client-side cache for company lookups (owner, 2026-10-13).
 *
 * Every search box (home, empty home, watchlist, screener, simulation, add
 * holding) calls searchStocks on each debounced keystroke. Typing "relia",
 * deleting a letter and retyping hit the server again, and on a cold instance
 * each of those waited the cold start. Three layers, cheapest first:
 *
 *   1. Exact hit — same normalised query and a limit at least as large.
 *   2. Prefix narrowing — a cached SHORTER prefix whose result was complete
 *      (fewer rows than its limit) already contains every match for the longer
 *      query, so filter it locally. "rel" (6 rows of 20) answers "reli".
 *   3. In-flight dedupe — two boxes asking the same thing share one request.
 *
 * Entries live 30 minutes in memory and are mirrored to sessionStorage so a
 * reload keeps them. Errors are never cached. Company names and tickers only;
 * nothing about the user's holdings is stored here.
 */

export interface SearchRow {
  ticker: string;
  company_name: string;
  isin?: string | null;
}
interface Entry { at: number; limit: number; rows: SearchRow[] }

const TTL_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 300;
const STORE_KEY = "ss.search.v1";

const mem = new Map<string, Entry>();
const inflight = new Map<string, Promise<SearchRow[]>>();
let hydrated = false;

export function normalise(q: string): string {
  return (q || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return;
    const now = Date.now();
    for (const [k, e] of Object.entries(JSON.parse(raw) as Record<string, Entry>)) {
      if (e && now - e.at < TTL_MS && Array.isArray(e.rows)) mem.set(k, e);
    }
  } catch { /* private mode, quota, corrupt: the cache is a convenience */ }
}

function persist(): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(Object.fromEntries(mem)));
  } catch { /* ignore */ }
}

function fresh(e: Entry | undefined): e is Entry {
  return !!e && Date.now() - e.at < TTL_MS;
}

function matches(row: SearchRow, q: string): boolean {
  return row.ticker.toLowerCase().includes(q) || (row.company_name || "").toLowerCase().includes(q)
    || (row.isin || "").toLowerCase().includes(q);
}

/** A cached answer for (q, limit) without a network call, or null. */
export function peek(q: string, limit: number): SearchRow[] | null {
  hydrate();
  const n = normalise(q);
  if (!n) return [];
  const exact = mem.get(n);
  if (fresh(exact) && exact.limit >= limit) return exact.rows.slice(0, limit);
  // Prefix narrowing: walk shorter prefixes, longest first.
  for (let i = n.length - 1; i >= 1; i--) {
    const e = mem.get(n.slice(0, i));
    if (fresh(e) && e.rows.length < e.limit) {
      return e.rows.filter((r) => matches(r, n)).slice(0, limit);
    }
  }
  return null;
}

function remember(n: string, limit: number, rows: SearchRow[]): void {
  mem.set(n, { at: Date.now(), limit, rows });
  if (mem.size > MAX_ENTRIES) {
    const oldest = [...mem.entries()].sort((a, b) => a[1].at - b[1].at).slice(0, mem.size - MAX_ENTRIES);
    for (const [k] of oldest) mem.delete(k);
  }
  persist();
}

/** Serve from cache, or fetch once (shared with any identical in-flight call). */
export async function cachedSearch(q: string, limit: number,
                                   fetcher: (q: string, limit: number) => Promise<SearchRow[]>): Promise<SearchRow[]> {
  const hit = peek(q, limit);
  if (hit) return hit;
  const n = normalise(q);
  const key = `${n}\u0000${limit}`;
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = fetcher(q, limit)
    .then((rows) => { remember(n, limit, rows || []); return rows || []; })
    .finally(() => { inflight.delete(key); });
  inflight.set(key, p);
  return p;
}

/** Tests only. */
export function __resetSearchCache(): void {
  mem.clear();
  inflight.clear();
  hydrated = false;
  try { sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}
